import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Trash2, Mail } from 'lucide-react';
import authService from '../../services/authService';
import CustomerSidebar from './CustomerSidebar';
import JobSection from './JobSection';
import UnlinkedInvoices from './UnlinkedInvoices';
import AddCustomerModal from '../AddCustomerModal';
import AddJobModal from '../AddJobModal';
import InvoiceModal from '../InvoiceList/components/InvoiceModal';
import InvoiceForm from '../InvoiceList/components/InvoiceForm';
import WoodwormGuaranteeForm from '../WoodwormGuaranteeForm';
import TreatmentReportEditor from '../TreatmentReportEditor';
import ConfirmDialog from '../ConfirmDialog';
import ComposeEmailModal from '../ComposeEmailModal';
import useConfirm from '../../hooks/useConfirm';
import { useToast } from '../Toast';
import jobService from '../../services/jobService';
import invoiceService from '../../services/invoiceService';
import estimateService from '../../services/estimateService';
import guaranteeService from '../../services/guaranteeService';
import appointmentService from '../../services/appointmentService';
import emailServiceFrontend from '../../services/emailService';
import customerService from '../../services/customerService';
import { getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from '../../App/services/appHelpers';
import { useMobileHeader } from '../../App/context/MobileHeaderContext';

const CustomerDetails = ({ customer: customerProp, onUpdateCustomer, onDeleteCustomer, onJobSelect, onNavigate, user, userProfile, onRefreshData }) => {
  const toast = useToast();
  const { confirmDialog, confirm } = useConfirm();
  const { setMobileSubtitleExtra, clearMobileSubtitleExtra } = useMobileHeader();
  const [fullCustomer, setFullCustomer] = useState(customerProp);
  const customer = fullCustomer || customerProp;

  // Push IDs into mobile header
  useEffect(() => {
    if (customer) {
      const refs = `${customer.customer_ref ? `ID: ${customer.customer_ref}  ` : ''}QB: ${customer.qb_customer_id || 'Awaiting Sync'}`;
      setMobileSubtitleExtra(refs);
    }
    return () => clearMobileSubtitleExtra();
  }, [customer?.customer_ref, customer?.qb_customer_id, setMobileSubtitleExtra, clearMobileSubtitleExtra]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [jobModalMode, setJobModalMode] = useState('job');
  const [editingJob, setEditingJob] = useState(null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Unlinked QB invoices (no job_id)
  const [unlinkedInvoices, setUnlinkedInvoices] = useState([]);

  // Invoice modal states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceFormContext, setInvoiceFormContext] = useState({ job: null, appointment: null });

  // Estimate modal states
  const [showEstimateForm, setShowEstimateForm] = useState(false);
  const [estimateFormContext, setEstimateFormContext] = useState({ job: null, appointment: null });

  // Treatment report modal states
  const [showReportEditor, setShowReportEditor] = useState(false);
  const [reportEditorContext, setReportEditorContext] = useState({ job: null, appointment: null });

  // Guarantee modal states
  const [showGuaranteeForm, setShowGuaranteeForm] = useState(false);
  const [guaranteeFormContext, setGuaranteeFormContext] = useState({ job: null, appointment: null });

  // Email compose states
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);

  // Document refresh key — increment to force JobCard document reload
  const [docRefreshKey, setDocRefreshKey] = useState(0);

  // Always fetch full customer data from API to ensure all fields are present
  useEffect(() => {
    const fetchFullCustomer = async () => {
      if (!customerProp?.id || !user) return;
      try {
        const franchiseeId = getFranchiseeIdForDataLoading(user);
        const result = await customerService.getCustomer(franchiseeId, customerProp.id);
        const fetched = result?.customer || result;
        if (fetched?.id) setFullCustomer(fetched);
      } catch (error) {
        console.error('Error fetching full customer:', error);
      }
    };
    fetchFullCustomer();
  }, [customerProp?.id, user]);

  // Load customer data
  useEffect(() => {
    if (customer) {
      loadCustomerJobs(customer.id);
      loadUnlinkedInvoices();
      loadSentEmails();
    }
  }, [customer]);

  const loadCustomerJobs = async (customerId) => {
    if (!user) return;
    setLoadingJobs(true);
    try {
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      const response = await jobService.getJobs(franchiseeId, { customer_id: customerId });
      setCustomerJobs(response.jobs || []);
    } catch (error) {
      console.error('Error loading customer jobs:', error);
      setCustomerJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadUnlinkedInvoices = async () => {
    try {
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      if (!franchiseeId || !customer?.qb_customer_id) {
        setUnlinkedInvoices([]);
        return;
      }
      const response = await invoiceService.getInvoicesByCustomerQbId(franchiseeId, customer.qb_customer_id);
      // Filter to only show QB invoices without job links
      const unlinked = (response.invoices || []).filter(inv => !inv.job_id);
      setUnlinkedInvoices(unlinked);
    } catch (error) {
      console.error('Error loading unlinked invoices:', error);
      setUnlinkedInvoices([]);
    }
  };

  const loadSentEmails = async () => {
    try {
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      if (!franchiseeId || !customer?.id) return;
      const result = await emailServiceFrontend.getCustomerEmails(franchiseeId, customer.id);
      setSentEmails(result.emails || []);
    } catch (error) {
      console.error('Error loading sent emails:', error);
      setSentEmails([]);
    }
  };

  // Email handlers
  const handleComposeEmail = useCallback(() => {
    setShowComposeEmail(true);
  }, []);

  const handleEmailSent = useCallback(() => {
    toast.success('Email sent successfully!');
    loadSentEmails();
  }, []);

  // Customer actions
  const handleEdit = () => setShowEditModal(true);
  const handleEditSubmit = (data) => {
    setFullCustomer(prev => ({ ...prev, ...data }));
    setShowEditModal(false);
    onUpdateCustomer({ ...data, id: customer.id, franchisee_id: customer.franchisee_id }).catch((error) => {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer');
    });
  };
  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete Customer',
      message: 'This will permanently delete this customer and all associated jobs. This cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (ok) onDeleteCustomer(customer.id);
  };

  // Job actions
  const handleAddJob = () => { setEditingJob(null); setJobModalMode('job'); setShowAddJobModal(true); };
  const handleAddContract = () => { setEditingJob(null); setJobModalMode('contract'); setShowAddJobModal(true); };
  const handleEditJob = (job) => { setEditingJob(job); setJobModalMode(job.category_path?.startsWith('Contract Services') ? 'contract' : 'job'); setShowAddJobModal(true); };

  const handleJobSubmit = async (jobData) => {
    if (!user) return;
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customer?.franchisee_id);
      const result = await jobService.createJob(franchiseeId, jobData);
      const createdJobId = result?.job?.id;

      if (createdJobId && jobData.schedule_first_appointment && jobData.appointment_date && jobData.appointment_time) {
        const scheduledDateTime = `${jobData.appointment_date}T${jobData.appointment_time}:00`;
        const durationMinutes = jobData.appointment_duration || 60;
        const startDate = new Date(scheduledDateTime);
        const endDate = new Date(startDate.getTime() + (durationMinutes * 60000));
        const scheduledEndDate = endDate.toISOString().slice(0, 19).replace('T', ' ');

        await appointmentService.createAppointment(franchiseeId, {
          job_id: createdJobId,
          scheduled_date: scheduledDateTime,
          scheduled_end_date: scheduledEndDate,
          estimated_duration: jobData.appointment_duration || null,
          appointment_type: 'first_visit',
          notes: jobData.appointment_notes || null
        });
      }

      setShowAddJobModal(false);
      await loadCustomerJobs(customer.id);
      onRefreshData && await onRefreshData();
      toast.success('Job created successfully');
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error(`Error creating job: ${error.message}`);
    }
  };

  const handleJobUpdate = async (jobData) => {
    if (!user || !editingJob) return;
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customer?.franchisee_id);
      await jobService.updateJob(franchiseeId, editingJob.id, jobData);

      if (jobData.schedule_first_appointment && jobData.appointment_date && jobData.appointment_time) {
        const scheduledDateTime = `${jobData.appointment_date}T${jobData.appointment_time}:00`;
        const durationMinutes = jobData.appointment_duration || 60;
        const startDate = new Date(scheduledDateTime);
        const endDate = new Date(startDate.getTime() + (durationMinutes * 60000));
        const scheduledEndDate = endDate.toISOString().slice(0, 19).replace('T', ' ');

        await appointmentService.createAppointment(franchiseeId, {
          job_id: editingJob.id,
          scheduled_date: scheduledDateTime,
          scheduled_end_date: scheduledEndDate,
          estimated_duration: jobData.appointment_duration || null,
          appointment_type: 'first_visit',
          notes: jobData.appointment_notes || null
        });
      }

      setShowAddJobModal(false);
      setEditingJob(null);
      await loadCustomerJobs(customer.id);
      onRefreshData && await onRefreshData();
      toast.success('Job updated successfully');
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error(`Error updating job: ${error.message}`);
    }
  };

  // Document creation actions
  const handleAddInvoice = useCallback((job, appointment) => {
    setInvoiceFormContext({ job, appointment });
    setShowInvoiceForm(true);
  }, []);

  const handleAddReport = useCallback((job, appointment) => {
    setReportEditorContext({ job, appointment });
    setShowReportEditor(true);
  }, []);

  const handleAddEstimate = useCallback((job, appointment) => {
    setEstimateFormContext({ job, appointment });
    setShowEstimateForm(true);
  }, []);

  const handleAddGuarantee = useCallback((job, appointment) => {
    setGuaranteeFormContext({ job, appointment });
    setShowGuaranteeForm(true);
  }, []);

  const handleInvoiceSubmit = async (invoiceData) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customer?.franchisee_id);

      // Add job_id and appointment_id
      const dataWithContext = {
        ...invoiceData,
        job_id: invoiceFormContext.job?.id || null,
        appointment_id: invoiceFormContext.appointment?.id || null
      };

      await invoiceService.createInvoice(franchiseeId, dataWithContext);
      setShowInvoiceForm(false);
      setInvoiceFormContext({ job: null, appointment: null });

      // Refresh documents in job cards + unlinked invoices
      setDocRefreshKey(prev => prev + 1);
      await loadUnlinkedInvoices();

      toast.success(`Invoice ${invoiceData.invoice_number || ''} created successfully`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(`Error creating invoice: ${error.message}`);
    }
  };

  const handleEstimateSubmit = async (estimateData) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customer?.franchisee_id);
      await estimateService.createEstimate(franchiseeId, estimateData);
      setShowEstimateForm(false);
      setEstimateFormContext({ job: null, appointment: null });

      setDocRefreshKey(prev => prev + 1);
      toast.success('Estimate created successfully');
    } catch (error) {
      console.error('Error creating estimate:', error);
      toast.error(`Error creating estimate: ${error.message}`);
    }
  };

  const handleReportSave = async () => {
    setShowReportEditor(false);
    setReportEditorContext({ job: null, appointment: null });

    setDocRefreshKey(prev => prev + 1);
  };

  const handleGuaranteeSubmit = async (guaranteeData) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customer?.franchisee_id);
      const result = await guaranteeService.createGuarantee(franchiseeId, guaranteeData);
      setShowGuaranteeForm(false);
      setGuaranteeFormContext({ job: null, appointment: null });

      setDocRefreshKey(prev => prev + 1);
      toast.success(`Guarantee ${result.guarantee_number} created successfully`);
    } catch (error) {
      console.error('Error creating guarantee:', error);
      toast.error(`Error creating guarantee: ${error.message}`);
    }
  };

  // View invoice detail
  const handleViewInvoice = async (invoice) => {
    try {
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      if (!franchiseeId) return;
      const detailed = await invoiceService.getInvoice(franchiseeId, invoice.id);
      setSelectedInvoice(detailed);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  if (!customer) {
    return (
      <div className="p-6 text-center py-12">
        <p className="text-gray-500">Customer not found</p>
      </div>
    );
  }

  return (
    <div className="px-2 pt-8 pb-2 md:p-6">
      {/* Header */}
      <div className="mb-3 lg:mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="hidden lg:block text-2xl font-bold text-gray-900">Customer Details</h1>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={handleComposeEmail} className="btn btn-md btn-secondary">
              <Mail size={15} /> <span className="hidden sm:inline">Email</span>
            </button>
            <button onClick={handleEdit} className="btn btn-md btn-secondary">
              <Edit2 size={15} /> <span className="hidden sm:inline">Edit</span>
            </button>
            {(userProfile?.role === 'admin' || authService.hasAdminReturn()) && (
              <button onClick={handleDelete} className="btn btn-md btn-danger">
                <Trash2 size={15} /> <span className="hidden sm:inline">Delete</span>
              </button>
            )}
          </div>
        </div>
        <p className="hidden lg:block text-lg text-gray-700 mt-1 ml-[34px]">{customer?.name}</p>
        <span className="hidden lg:block text-xs font-mono text-gray-400 mt-0.5 ml-[34px]">
          {customer?.customer_ref && <>ID: {customer.customer_ref} &nbsp;</>}
          QB: {customer?.qb_customer_id || 'Awaiting Sync'}
        </span>
      </div>

      {/* Mobile: Customer info first, then jobs */}
      <div className="lg:hidden mb-6">
        <CustomerSidebar customer={customer} onComposeEmail={handleComposeEmail} sentEmails={sentEmails} onEmailClick={onNavigate ? (email) => onNavigate('emails', null, { emailSearch: email.subject }) : undefined} />
      </div>

      {/* Main 2-column layout: Jobs (left, wider) | Customer sidebar (right) */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Jobs */}
        <div className="flex-1 lg:w-2/3">
          <JobSection
            jobs={customerJobs}
            customer={customer}
            user={user}
            loadingJobs={loadingJobs}
            onAddJob={handleAddJob}
            onAddContract={handleAddContract}
            onJobSelect={onJobSelect}
            onViewInvoice={handleViewInvoice}
            docRefreshKey={docRefreshKey}
          />

          {/* Unlinked QB Invoices */}
          <UnlinkedInvoices
            invoices={unlinkedInvoices}
            onViewInvoice={handleViewInvoice}
          />
        </div>

        {/* Right: Customer sidebar (desktop only, mobile shows above) */}
        <div className="hidden lg:block lg:w-1/3">
          <CustomerSidebar customer={customer} onComposeEmail={handleComposeEmail} sentEmails={sentEmails} onEmailClick={onNavigate ? (email) => onNavigate('emails', null, { emailSearch: email.subject }) : undefined} />
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <AddCustomerModal
          customer={customer}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEditSubmit}
          isEditing={true}
          userProfile={userProfile}
        />
      )}

      {showAddJobModal && (
        <AddJobModal
          onClose={() => { setShowAddJobModal(false); setEditingJob(null); setJobModalMode('job'); }}
          onSubmit={editingJob ? handleJobUpdate : handleJobSubmit}
          job={editingJob}
          isEditing={!!editingJob}
          preSelectedCustomer={customer}
          user={user}
          mode={jobModalMode}
          onAppointmentChange={() => loadCustomerJobs(customer.id)}
        />
      )}

      {showInvoiceForm && (
        <InvoiceForm
          onClose={() => { setShowInvoiceForm(false); setInvoiceFormContext({ job: null, appointment: null }); }}
          onSubmit={handleInvoiceSubmit}
          preselectedCustomer={customer}
          preselectedJob={invoiceFormContext.job}
          preselectedAppointment={invoiceFormContext.appointment}
          showCustomerInTitle={true}
          user={user}
        />
      )}

      <InvoiceModal
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
      />

      {showEstimateForm && (
        <InvoiceForm
          mode="estimate"
          onClose={() => { setShowEstimateForm(false); setEstimateFormContext({ job: null, appointment: null }); }}
          onSubmit={handleEstimateSubmit}
          preselectedCustomer={customer}
          preselectedJob={estimateFormContext.job}
          preselectedAppointment={estimateFormContext.appointment}
          showCustomerInTitle={true}
          user={user}
        />
      )}

      {showGuaranteeForm && (
        <WoodwormGuaranteeForm
          onClose={() => { setShowGuaranteeForm(false); setGuaranteeFormContext({ job: null, appointment: null }); }}
          onSubmit={handleGuaranteeSubmit}
          preselectedCustomer={customer}
          preselectedJob={guaranteeFormContext.job}
          preselectedAppointment={guaranteeFormContext.appointment}
          user={user}
        />
      )}

      {showReportEditor && (
        <TreatmentReportEditor
          onSave={handleReportSave}
          onCancel={() => { setShowReportEditor(false); setReportEditorContext({ job: null, appointment: null }); }}
          userProfile={userProfile}
          initialContext={{
            customer,
            job: reportEditorContext.job,
            appointment: reportEditorContext.appointment
          }}
        />
      )}
      {showComposeEmail && (
        <ComposeEmailModal
          customer={customer}
          user={user}
          userProfile={userProfile}
          jobs={customerJobs}
          onClose={() => setShowComposeEmail(false)}
          onSent={handleEmailSent}
        />
      )}

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default CustomerDetails;
